import { observer } from "mobx-react-lite"
import type { ReactNode } from "react"
import { useCallback, useEffect, useMemo } from "react"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { cn } from "@/lib/utils"
import { TabsStore } from "@/stores/TabsStore"

export interface TabDefinition {
  id: string
  label: string
  icon?: ReactNode
  content: ReactNode
  disabled?: boolean
}

export interface TabsWithStoreProps {
  /**
   * Array of tab definitions
   */
  tabs: TabDefinition[]

  /**
   * Currently selected tab ID (controlled mode)
   * If provided, component operates in controlled mode
   */
  selectedTab?: string

  /**
   * Default selected tab ID (uncontrolled mode)
   * Only used when selectedTab is not provided
   */
  defaultSelectedTab?: string

  /**
   * Callback function called when tab selection changes
   */
  onTabChange?: (tabId: string) => void

  /**
   * Additional CSS classes for the root Tabs element
   */
  className?: string

  /**
   * Additional CSS classes for the TabsList element
   */
  listClassName?: string

  /**
   * Additional CSS classes for TabsTrigger elements
   */
  triggerClassName?: string

  /**
   * Additional CSS classes for TabsContent elements
   */
  contentClassName?: string

  /**
   * Whether the tabs list should take full width of the container
   */
  fullWidth?: boolean
}

/**
 * TabsWithStore - Easy-to-use tabs component with MobX store integration
 *
 * Features:
 * - Controlled mode: Pass selectedTab prop to control from parent
 * - Uncontrolled mode: Use defaultSelectedTab for internal state management
 * - Callback support: onTabChange fired when tab selection changes
 * - Optimized rendering: Uses observer pattern to prevent unnecessary re-renders
 * - Type-safe: Full TypeScript support
 *
 * @example Controlled mode
 * ```tsx
 * const [activeTab, setActiveTab] = useState("tab1")
 *
 * <TabsWithStore
 *   tabs={[
 *     { id: "tab1", label: "Tab 1", content: <div>Content 1</div> },
 *     { id: "tab2", label: "Tab 2", content: <div>Content 2</div> }
 *   ]}
 *   selectedTab={activeTab}
 *   onTabChange={setActiveTab}
 * />
 * ```
 *
 * @example Uncontrolled mode
 * ```tsx
 * <TabsWithStore
 *   tabs={[
 *     { id: "tab1", label: "Tab 1", content: <div>Content 1</div> },
 *     { id: "tab2", label: "Tab 2", content: <div>Content 2</div> }
 *   ]}
 *   defaultSelectedTab="tab1"
 *   onTabChange={(tabId) => console.log("Tab changed to:", tabId)}
 * />
 * ```
 */
export const TabsWithStore = observer(
  ({
    tabs,
    selectedTab,
    defaultSelectedTab,
    onTabChange,
    className,
    listClassName,
    triggerClassName,
    contentClassName,
    fullWidth = false,
  }: TabsWithStoreProps) => {
    // Create stable tabs key for dependency tracking
    const tabsKey = useMemo(
      () => tabs.map((t) => `${t.id}-${t.disabled || false}`).join(","),
      [tabs],
    )

    // Create store instance - memoized to prevent recreation on every render
    // biome-ignore lint/correctness/useExhaustiveDependencies: We intentionally use tabsKey instead of tabs to prevent unnecessary store recreation when only content changes
    const store = useMemo(
      () =>
        TabsStore.create({
          tabs: tabs.map((tab) => ({
            id: tab.id,
            label: tab.label,
            disabled: tab.disabled || false,
          })),
          selectedTab: selectedTab || defaultSelectedTab,
        }),
      // Only recreate store if tabs structure changes (not content)
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [tabsKey, selectedTab, defaultSelectedTab],
    )

    // Sync controlled prop to store
    useEffect(() => {
      if (selectedTab !== undefined && selectedTab !== store.selectedTab) {
        store.setSelectedTab(selectedTab)
      }
    }, [selectedTab, store])

    // Handle tab change with callback
    const handleTabChange = useCallback(
      (value: string) => {
        // Update store
        store.setSelectedTab(value)

        // Call callback if provided
        onTabChange?.(value)
      },
      [store, onTabChange],
    )

    // Get current tab value from store
    const currentValue = store.currentTab

    return (
      <Tabs value={currentValue} onValueChange={handleTabChange} className={className}>
        <TabsList className={cn(fullWidth && "w-full", listClassName)}>
          {tabs.map((tab) => (
            <TabsTrigger
              key={tab.id}
              value={tab.id}
              disabled={tab.disabled}
              className={triggerClassName}
            >
              {tab.icon && <span className="shrink-0">{tab.icon}</span>}
              {tab.label}
            </TabsTrigger>
          ))}
        </TabsList>

        {tabs.map((tab) => (
          <TabsContent key={tab.id} value={tab.id} className={contentClassName}>
            {tab.content}
          </TabsContent>
        ))}
      </Tabs>
    )
  },
)

TabsWithStore.displayName = "TabsWithStore"
